import {
  CellValues,
  ColumnHeading,
  CustomSortKeyFnMap,
  EntityFilterControlsConfig,
  inactiveRowStyles,
  PaginatedDataTable,
  TableCellStylesFn,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { formatDMYWithTime } from '@lbcamden/shared-utils';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { AddNewButton } from '~/client/components/common/Button/AddNewButton';
import { SectionContainer } from '~/client/components/common/SectionContainer';
import { useCaseList, useUserStore } from '~/client/hooks/storeHooks';
import { getCaseStatus } from '~/client/utils/cases';
import { toCaseCreatePage, toCasePage } from '~/client/utils/navigation';
import { Case } from '~/common/types/cases';
import { Person } from '~/common/types/people';
import { getPersonNameSortKey } from '~/common/utils/sort';

type Props = {
  propertyId: number;
};

export type ColumnId = (typeof headings)[number]['id'];

const headings = [
  { id: 'caseId', content: 'Case number' },
  { id: 'demand', content: 'Type' },
  { id: 'person', content: 'Regarding' },
  { id: 'reported', content: 'Reported' },
  { id: 'isClosed', content: 'Status' },
] as const satisfies ColumnHeading[];

const getCustomSortKeyFnMap = (
  getPersonFromId: (personId: number) => Person | undefined,
): CustomSortKeyFnMap<Case, ColumnId> => ({
  person: (item) => {
    const person = getPersonFromId(item.personId);
    return person && getPersonNameSortKey(person);
  },
});

const filterConfigs: EntityFilterControlsConfig<Case> = [
  [
    {
      type: 'toggle',
      key: 'isClosed',
      activeValue: false,
      label: 'Open cases',
    },
    {
      type: 'toggle',
      key: 'isClosed',
      label: 'Closed cases',
    },
  ],
];

const getMapCaseToRow =
  (getPersonFromId: (personId: number) => Person | undefined) =>
  (caseItem: Case): CellValues<ColumnId> => ({
    ...caseItem,
    _rowId: String(caseItem.caseId),
    demand: caseItem.isClosed ? `${caseItem.demand} (closed)` : caseItem.demand,
    person: getPersonFromId(caseItem.personId)?.fullName,
    reported: formatDMYWithTime(caseItem.reported || caseItem.created),
    isClosed: getCaseStatus(caseItem),
  });

const getCellStyles =
  (cases: Case[]): TableCellStylesFn =>
  (columnId, caseId) => {
    const rowCase = cases.find((caseItem) => caseId && caseItem.caseId === parseInt(caseId));
    const { isClosed } = rowCase || {};

    return [
      { width: 150 },
      { caseId: { width: 120 }, demand: { width: 250 } }[columnId!],
      isClosed && inactiveRowStyles,
    ];
  };

export const Cases: FunctionComponent<Props> = ({ propertyId }) => {
  const navigate = useNavigate();

  const userStore = useUserStore();
  const cases = useCaseList(propertyId);

  const getPersonFromId = useCallback(
    (personId: number) => userStore?.get('people', personId) as Person,
    [userStore],
  );

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: headings.slice(1, 2),
    sm: headings.slice(1, 2),
    md: headings.slice(0, 4),
  });

  const customSortKeyFnMap = useMemo(
    () => getCustomSortKeyFnMap(getPersonFromId),
    [getPersonFromId],
  );

  const onAddNew = () => navigate(toCaseCreatePage(propertyId));

  const onClickRow = useCallback(
    ({ caseId }: Case) => navigate(toCasePage(propertyId, caseId)),
    [],
  );

  return (
    <SectionContainer
      title="Cases"
      headerActions={<AddNewButton label="Create new case" onClick={onAddNew} />}
    >
      <PaginatedDataTable
        cellStyles={getCellStyles(cases)}
        headings={visibleHeadings}
        filterConfigs={filterConfigs}
        items={cases}
        defaultSort={{ field: 'reported', direction: 'desc' }}
        customSortKeyFnMap={customSortKeyFnMap}
        mapItemToRow={getMapCaseToRow(getPersonFromId)}
        onClickRow={onClickRow}
      />
    </SectionContainer>
  );
};
